import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import PdfReportApi from "src/services/apis/pdfReport";
import Toast from "src/components/Common/Toast";
import { searchWith } from "./ProctoringReview/clock";
import { bucketOfQuestion, traitIdsOf } from "./questionLink";

export const QUESTION_PARAM = "question";
export const QUESTION_NOT_SHOWN = "This question is not shown in the report's question lists.";

/**
 * Opens the question named by ?question=<id>: finds the first drill-down bucket
 * that lists it (one quiz-details call for all their traits), opens that bucket
 * and hands back the question to scroll to. The param is then removed with a
 * replace, so Back returns to the entry before the link (e.g. the review room).
 *
 * @param {Object} o
 * @param {string} o.linkId
 * @param {Array} o.buckets  the drill-down buckets, in display order
 * @param {boolean} o.ready  the report data and the position config are in
 * @param {boolean} o.roomOpen  the review room is open; the link waits until it closes
 * @param {boolean} o.canOpen  this report shows question lists (not mini, PDF or hideHeader)
 * @param {Function} o.openBucket  (bucketId) => void
 * @returns {{focusQuestionId: string|null, clearFocus: Function}}
 */
export default function useQuestionLink({ linkId, buckets, ready, roomOpen, canOpen, openBucket }) {
  const history = useHistory();
  const { search } = useLocation();
  const questionId = new URLSearchParams(search).get(QUESTION_PARAM);
  const [focusQuestionId, setFocusQuestionId] = useState(null);
  const busy = useRef(false);
  const mounted = useRef(true);

  useEffect(() => () => {
    mounted.current = false;
  }, []);

  useEffect(() => {
    if (!questionId || roomOpen || !ready || busy.current) return;

    const removeParam = () => {
      const loc = history.location;
      history.replace({ pathname: loc.pathname, search: searchWith(loc.search, { [QUESTION_PARAM]: null }), hash: loc.hash });
    };

    if (!canOpen) {
      removeParam();
      return;
    }

    busy.current = true;
    (async () => {
      let bucket = null;
      try {
        const traitIds = traitIdsOf(buckets);
        if (traitIds.length) {
          const response = await PdfReportApi.getQuizDetailsFromTraits(linkId, traitIds.join(","));
          if (response?.success) bucket = bucketOfQuestion(buckets, response.data, questionId);
        }
      } catch (err) {
        console.error(err);
      }
      if (!mounted.current) return;
      if (bucket) {
        openBucket(bucket.id);
        setFocusQuestionId(questionId);
      } else {
        Toast("info", QUESTION_NOT_SHOWN, "Question not in the report");
      }
      removeParam();
      busy.current = false;
    })();
  }, [questionId, roomOpen, ready, canOpen, buckets, linkId, history, openBucket]);

  const clearFocus = useCallback(() => setFocusQuestionId(null), []);

  return { focusQuestionId, clearFocus };
}
